import { useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../context/AuthContext';
import useMsal from '../../auth/useMsal';
import { LoginWrapper, LoginContent, ErrorMessage, LoadingOverlay, Spinner, LoadingText, AccountInfo, SignOutButton, ClearCacheLink } from './LoginPage.styles';

interface LocationState {
    from?: {
        pathname: string;
        search?: string;
    };
}

const Login = () => {
    const { loginAzAd, logoutAzAd } = useMsal();
    const navigate = useNavigate();
    const location = useLocation();
    const { loggedInUser, roles, rolesVerified, isLoadingRoles, isVerifyingToken, isAuthenticating } = useAuthStore();
    const [searchParams, setSearchParams] = useSearchParams();
    const errorMessage = searchParams.get('error');
    const accountName = searchParams.get('name');
    const accountEmail = searchParams.get('account');

    // Get the original URL the user was trying to access (if any)
    const state = location.state as LocationState;
    const from = state?.from;
    const returnUrl = from ? `${from.pathname}${from.search || ''}` : '/products';

    // Determine if we're in a loading state
    const isLoading = isVerifyingToken || isAuthenticating || (loggedInUser && !rolesVerified);

    // If user is authenticated AND roles have been verified with at least one role, redirect to original URL or home
    useEffect(() => {
        if (loggedInUser && rolesVerified && roles.length > 0) {
            navigate(returnUrl, { replace: true });
        }
    }, [loggedInUser, rolesVerified, roles, navigate, returnUrl]);

    const handleSignIn = () => {
        // Clear error message from URL before login attempt
        if (errorMessage) {
            setSearchParams({}, { replace: true });
        }
        loginAzAd();
    };

    const handleClearCache = (e: React.MouseEvent) => {
        e.preventDefault();
        // Clear all MSAL / auth entries from localStorage
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.startsWith('msal.') || key.includes('login.windows.net') || key.includes('554f3972') || key === 'msal.token.keys')) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((k) => localStorage.removeItem(k));
        localStorage.removeItem('cb_app_version');
        sessionStorage.clear();
        // Unregister service workers
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistrations().then((regs) => regs.forEach((r) => r.unregister()));
        }
        // Clear Cache Storage
        if ('caches' in window) {
            caches.keys().then((names) => names.forEach((n) => caches.delete(n)));
        }
        // Hard reload
        window.location.replace(window.location.origin + '/login');
    };

    // Determine loading message
    const getLoadingMessage = () => {
        if (isVerifyingToken) return 'Verifying authentication...';
        if (isAuthenticating) return 'Authenticating...';
        if (isLoadingRoles) return 'Verifying permissions...';
        return 'Loading...';
    };

    return (
        <>
            <LoginWrapper>
                <LoginContent>
                    <h1>Commodity Book</h1>
                    <div>
                        <p>Please click on the button below to sign in.</p>
                        <button onClick={handleSignIn} disabled={isLoading}>
                            Sign in
                        </button>
                        <ClearCacheLink href="#" onClick={handleClearCache}>
                            Having trouble logging in? Clear cache &amp; reload
                        </ClearCacheLink>
                    </div>
                </LoginContent>
                {errorMessage && (
                    <ErrorMessage>
                        {errorMessage === 'unauthorized' && (
                            <>
                                You are not authorized to access this application.
                                {accountEmail && (
                                    <AccountInfo>
                                        Account: {accountName && `${accountName} `}({accountEmail})
                                    </AccountInfo>
                                )}
                                Please contact your administrator.
                                <SignOutButton onClick={logoutAzAd}>Sign out</SignOutButton>
                            </>
                        )}
                        {errorMessage === 'no-roles' && (
                            <>
                                No roles assigned to your account.
                                {accountEmail && (
                                    <AccountInfo>
                                        Account: {accountName && `${accountName} `}({accountEmail})
                                    </AccountInfo>
                                )}
                                Please contact your administrator to request access.
                                <SignOutButton onClick={logoutAzAd}>Sign out</SignOutButton>
                            </>
                        )}
                        {errorMessage !== 'unauthorized' && errorMessage !== 'no-roles' && errorMessage}
                    </ErrorMessage>
                )}
            </LoginWrapper>
            {isLoading && (
                <LoadingOverlay>
                    <Spinner />
                    <LoadingText>{getLoadingMessage()}</LoadingText>
                </LoadingOverlay>
            )}
        </>
    );
};

export default Login;
